/**
 * Supplier preview-image selection heuristic (pure, unit-testable).
 *
 * The Supplier & SKU editor tab shows a single "hero" preview image derived
 * from the chosen SKU's ``supplierMeta.image_urls_by_color`` map -- a
 * ``{ colorName: [url, url, ...] }`` map of bare URL strings.
 *
 * SanMar encodes view + model-state in the URL FILENAME (confirmed live for
 * SKU 637167); the frontend receives only bare URLs, so we infer both from
 * the filename rather than depending on any ``view`` field:
 *   637167_black_flat_front.jpg  -> sans-model ("flat"), front view
 *   637167_black_model_front.jpg -> on-model, front view
 *   ..._flat_back / ..._model_side / etc.
 * Rule: ``_flat_`` = sans-model, ``_model_`` = on-model; ``_front`` /
 * ``_back`` / ``_side`` = view. All matching is case-insensitive on the URL.
 *
 * Selection algorithm (locked -- Jake's spec):
 *   1. Neutral color priority (case-insensitive substring match against the
 *      color name), in order: black, grey/gray, white, navy, blue, red
 *      (grey OR gray; navy/blue both satisfy the blue slot).
 *   2. First choice: the first neutral (in priority order) that has an image
 *      matching BOTH ``_flat_`` (sans-model) AND ``_front``.
 *   3. Else: the first color (neutrals-first, then remaining colors in map
 *      order) with ANY ``_flat_front`` image -- broadens past neutrals but
 *      still prefers sans-model front.
 *   4. Else: the first color (same neutral-first iteration) with ANY
 *      ``_front`` image (includes on-model front, e.g. ``_model_front``).
 *   5. Else: the first NON-back image (``_side`` or untagged), same
 *      neutral-first iteration. Caps (e.g. STC60) ship no ``_front`` image --
 *      only ``_back`` and ``_side`` -- so a plain "first image" fallback lands
 *      on a back shot (issue #2225). We never prefer an explicit ``_back``
 *      while any non-back image exists for a selectable color.
 *   6. Else: the first image URL of the first color that has any image
 *      (same neutral-first iteration) -- back-only last resort.
 *   7. Else (empty map / no usable image): null -> caller renders a
 *      neutral placeholder tile.
 */

// Neutral color priority list. Substrings are matched case-insensitively
// against each color NAME. "grey"/"gray" are folded to the same slot below;
// "navy" and "blue" both count for the blue slot -- both appear here so a
// color named either way is treated as a neutral.
export const DEFAULT_NEUTRAL_PRIORITY = Object.freeze([
    'black',
    'grey',
    'white',
    'navy',
    'blue',
    'red',
])

// Match a neutral token against a color name, case-insensitively. "grey"
// also matches "gray" (and vice versa) so the grey slot is spelling-agnostic.
function colorMatchesToken(colorName, token) {
    const name = String(colorName).toLowerCase()
    const t = token.toLowerCase()
    if (t === 'grey' || t === 'gray') {
        return name.includes('grey') || name.includes('gray')
    }
    return name.includes(t)
}

// URL predicates -- infer view + model-state from the filename.
function isFront(url) {
    return typeof url === 'string' && url.toLowerCase().includes('_front')
}

// An explicit back shot. Caps carry no _front image, only _back + _side, so
// we treat _back as a last-resort view: never chosen while a non-back image
// (side / untagged / front) exists for a selectable color (issue #2225).
function isBack(url) {
    return typeof url === 'string' && url.toLowerCase().includes('_back')
}

function isFlat(url) {
    return typeof url === 'string' && url.toLowerCase().includes('_flat_')
}

function isFlatFront(url) {
    return isFlat(url) && isFront(url)
}

function firstUsableUrl(urls) {
    if (!Array.isArray(urls)) return null
    for (const url of urls) {
        if (typeof url === 'string' && url.length > 0) return url
    }
    return null
}

/**
 * Order the map's color names neutrals-first (in priority order), then any
 * remaining colors in their original map-insertion order. A color that
 * matches an earlier neutral token wins that slot; each color appears once.
 */
function orderedColorNames(map, neutralPriority) {
    const allNames = Object.keys(map)
    const ordered = []
    const taken = new Set()

    for (const token of neutralPriority) {
        for (const name of allNames) {
            if (taken.has(name)) continue
            if (colorMatchesToken(name, token)) {
                ordered.push(name)
                taken.add(name)
            }
        }
    }
    for (const name of allNames) {
        if (!taken.has(name)) {
            ordered.push(name)
            taken.add(name)
        }
    }
    return ordered
}

/**
 * Pick a single preview-image URL from an image-urls-by-color map.
 *
 * @param {Object<string, string[]>|null|undefined} imageUrlsByColor
 *   Map of color name -> array of bare image URL strings.
 * @param {string[]} [neutralPriority=DEFAULT_NEUTRAL_PRIORITY]
 *   Ordered neutral tokens (case-insensitive substrings of color names).
 * @returns {string|null} the chosen URL, or null when no usable image exists.
 */
export function pickPreviewImage(
    imageUrlsByColor,
    neutralPriority = DEFAULT_NEUTRAL_PRIORITY,
) {
    if (
        !imageUrlsByColor
        || typeof imageUrlsByColor !== 'object'
        || Array.isArray(imageUrlsByColor)
    ) {
        return null
    }

    const names = orderedColorNames(imageUrlsByColor, neutralPriority)
    if (names.length === 0) return null

    // Steps 2 + 3: first color (neutrals-first) with a flat-front image.
    // Because `names` is already neutral-ordered, a single pass gives step 2
    // (neutral flat-front) precedence and falls through to step 3
    // (non-neutral flat-front) automatically.
    for (const name of names) {
        const urls = imageUrlsByColor[name]
        if (!Array.isArray(urls)) continue
        const hit = urls.find((u) => typeof u === 'string' && isFlatFront(u))
        if (hit) return hit
    }

    // Step 4: first color (same iteration) with ANY front image (incl.
    // on-model front).
    for (const name of names) {
        const urls = imageUrlsByColor[name]
        if (!Array.isArray(urls)) continue
        const hit = urls.find((u) => typeof u === 'string' && isFront(u))
        if (hit) return hit
    }

    // Step 5: first NON-back image (side / untagged), same iteration. A cap
    // has no _front image, only _back + _side; without this step the plain
    // first-image fallback (step 6) would land on the back shot. Never prefer
    // an explicit _back while any non-back image exists.
    for (const name of names) {
        const urls = imageUrlsByColor[name]
        if (!Array.isArray(urls)) continue
        const hit = urls.find(
            (u) => typeof u === 'string' && u.length > 0 && !isBack(u),
        )
        if (hit) return hit
    }

    // Step 6: first usable image of the first color that has any -- back-only
    // last resort (every usable image is an explicit _back).
    for (const name of names) {
        const hit = firstUsableUrl(imageUrlsByColor[name])
        if (hit) return hit
    }

    // Step 7: nothing usable.
    return null
}
